/**
 * Single source of truth for all supported course languages and their voices.
 *
 * The tool speaks English. The course speaks whatever language the creator chooses.
 *
 * TTS providers:
 *   - 'openai'  → OpenAI TTS (English US + European languages) → MP3
 *   - 'sarvam'  → Sarvam Bulbul v3 (Indian languages + English India) → WAV
 *
 * Each language has TWO voices: female + male. The course picks a default
 * gender; individual narration segments can override (see narration drawer).
 */

export type VoiceGender = 'female' | 'male'

export interface VoiceConfig {
  provider: 'openai' | 'sarvam'
  voice: string           // OpenAI voice name OR Sarvam speaker name
  model: string           // OpenAI model OR Sarvam model ('bulbul:v3')
  langCode: string        // Language code for the TTS API (may differ from app code)
  mimeType: string        // 'audio/mpeg' (OpenAI) or 'audio/wav' (Sarvam)
}

export interface Language {
  code: string                      // app language code, e.g. 'en', 'en-IN', 'hi', 'ta'
  label: string                     // English display name
  nativeLabel: string               // Name in its own script
  region: 'global' | 'indian' | 'european' | 'east-asian'
  voices: { female: VoiceConfig; male: VoiceConfig }
  /**
   * Course-narration TTS voices (Content Creator surface: NarrationDrawer,
   * wizard preview, /play/, translate/clone jobs). When present, these WIN
   * over `voices` for narration via getNarrationVoiceConfig(). When absent,
   * narration falls back to `voices`.
   *
   * Exists because the two products need different voices for the same
   * language: Role Play V2 runs Indian languages + en-IN on OpenAI
   * gpt-4o-mini-tts (turn-1 prefetch and turn-2+ hybrid must share one
   * speaker), but course narration needs Sarvam Bulbul v3 for the Indian
   * accent — including Indian-accented English words inside multilingual
   * scripts. Dropping Sarvam from `voices` in the 2026-07-09 catalog
   * rationalisation silently switched narration to a Western accent
   * (prod regression, fixed 2026-07-17).
   */
  narrationVoices?: { female: VoiceConfig; male: VoiceConfig }
  /** Sample line played in the voice picker. Falls back to ENGLISH_SAMPLE if absent. */
  sampleText?: string
  /**
   * How the voice path is delivered for this language. Selected at the
   * language level so a single switch in PlayPage can route the right
   * way per session:
   *   - 'openai-pipeline'  → Whisper STT + customerTurn (Claude) + TTS-1
   *   - 'sarvam-pipeline'  → Saarika STT + customerTurn (Claude) + Bulbul v3 TTS
   *   - 'openai-realtime'  → OpenAI Realtime WebSocket (STT + LLM + TTS in
   *                          one bidirectional stream). Activates only on
   *                          voice surfaces (Customer Call, Customer Retail);
   *                          chat and coach-led modes fall back to the
   *                          language's voices[] config for any TTS calls.
   *
   * The `voices` field is still used for non-Realtime paths AND as the
   * fallback voice when a Realtime language is used in a non-voice mode.
   */
  voiceTransport: 'openai-pipeline' | 'sarvam-pipeline' | 'openai-realtime'

  // ── Hybrid v2 fields (added 2026-06-29) ────────────────────────────────
  // Used by Tamil Hybrid v2 and any future "brain-in-A, speak-in-B"
  // language. When absent, behaviour is unchanged. All optional.
  //
  // brainModel: OpenAI model the customer brain uses for THIS language.
  //   Default if undefined: dialogueHybrid's hard-coded 'gpt-4.1-mini'.
  //   Tamil Hybrid v2 sets this to 'gpt-4o' so the English thought layer
  //   is sharper before being translated.
  //
  // brainOutputLanguage: the language the brain is INSTRUCTED to reply in
  //   (passed to the persona builder as the # Language section). Distinct
  //   from `label`, which is the display name shown to the author. When
  //   the brain thinks in one language and the customer speaks another
  //   (Tamil Hybrid v2: brain=English, customer=Tamil via translation),
  //   this field carries the brain-side language.
  //   Default if undefined: same as `label` (brain replies in the user-
  //   facing language; no translation step).
  //
  // translationStep: optional post-brain text transform. When set, the
  //   brain's English reply is translated to the target language before
  //   reaching TTS. Currently the only value is 'sarvam-mayura'
  //   (Sarvam Mayura v1, mode=modern-colloquial internally, labelled
  //   "Business Colloquial" in user-facing copy).
  brainModel?: string
  brainOutputLanguage?: string
  /**
   * ISO-639-1 code passed to the OpenAI Realtime STT as the source
   * language hint. When the learner is expected to speak Tamil,
   * setting this to 'ta' stops the STT mis-routing Tamil audio as
   * English (which leads to garbled English / Hindi-script hallucinations).
   * Defaults to 'en' when undefined.
   */
  sttSourceLanguage?: string
  translationStep?: {
    provider: 'sarvam-mayura'
    targetLang: string                // e.g. 'ta-IN'
    /** Sarvam API mode value. We use 'modern-colloquial' for natural
     *  enterprise-style Tamil with English code-switching. The user-
     *  facing label for this in marketing/UI copy is "Business
     *  Colloquial". */
    mode: 'modern-colloquial' | 'classic-colloquial' | 'formal' | 'code-mixed'
  }
}

// ── Voice samples ─────────────────────────────────────────────────────────────

const ENGLISH_SAMPLE = 'Welcome to Learnbee. I\'ll guide you through this course with clarity and warmth.'

// ── OpenAI gpt-4o-mini-tts voices ────────────────────────────────────────────
// Nova (female) / Onyx (male) — the two voices used across all 16
// canonical language records. gpt-4o-mini-tts supports the `instructions`
// prosody parameter used by the streaming TTS path.
//
// The legacy `openai(...)` HTTP-poll TTS-1 helpers were removed
// 2026-07-09 when Request 3 rationalised the catalog. Sarvam Bulbul v3
// was removed at the same time but restored 2026-07-17 as
// `narrationVoices` (see below) — course narration was still using it.
// Role Play's `voices` remain OpenAI-only for realtime languages.
const O_RT_F = { provider: 'openai', voice: 'nova', model: 'gpt-4o-mini-tts', mimeType: 'audio/mpeg' } as const
const O_RT_M = { provider: 'openai', voice: 'onyx', model: 'gpt-4o-mini-tts', mimeType: 'audio/mpeg' } as const

function openaiRt(langCode: string): { female: VoiceConfig; male: VoiceConfig } {
  return {
    female: { ...O_RT_F, langCode },
    male:   { ...O_RT_M, langCode },
  }
}

// ── Sarvam Bulbul v3 speakers (course narration only) ────────────────────────
// priya (F) / kabir (M) — confirmed valid bulbul:v3 speakers, language-
// agnostic across all Indian languages + en-IN. Restored 2026-07-17 as
// `narrationVoices` after the 2026-07-09 catalog rationalisation removed
// them from `voices` and course narration lost its Indian accent.
const S_F = { provider: 'sarvam', voice: 'priya', model: 'bulbul:v3', mimeType: 'audio/wav' } as const
const S_M = { provider: 'sarvam', voice: 'kabir', model: 'bulbul:v3', mimeType: 'audio/wav' } as const

function sarvam(langCode: string): { female: VoiceConfig; male: VoiceConfig } {
  return {
    female: { ...S_F, langCode },
    male:   { ...S_M, langCode },
  }
}

// ── Language catalog ─────────────────────────────────────────────────────────
//
// Rationalised 2026-07-09 (Request 3). 16 canonical entries, one per
// language, each on its best-known architecture. Old aliases (`-rt`,
// `-hybrid`, "legacy alias", "Business Colloquial") are dropped from the
// catalog; existing DB rows are remapped by migration 17 to these codes.
// LEGACY_CODE_REMAP below covers any row that slips past the migration.
//
// Architectures used:
//   - Full OpenAI RT       — English family + European + Japanese
//     STT: OpenAI Realtime   Brain: gpt-4.1-mini   TTS: gpt-4o-mini-tts
//     Expected audible ~4 s.
//   - Hybrid (openai-sarvam-openai) — 9 Indian languages (hi..te + ta)
//     STT: OpenAI Realtime   Brain: gpt-4o (replies in English)
//     Translate: Sarvam Mayura (classic-colloquial)   TTS: gpt-4o-mini-tts
//     Expected audible ~5 s.

export const LANGUAGES: Language[] = [
  // ── English family ───────────────────────────────────────────────────
  {
    code: 'en', label: 'English', nativeLabel: 'English',
    region: 'global', voices: openaiRt('en'), voiceTransport: 'openai-realtime',
    sampleText: ENGLISH_SAMPLE,
  },
  {
    code: 'en-IN', label: 'English (India)', nativeLabel: 'English (India)',
    region: 'indian', voices: openaiRt('en'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('en-IN'),
    sttSourceLanguage: 'en',
    sampleText: ENGLISH_SAMPLE,
  },

  // ── Indian languages — Hybrid pipeline ───────────────────────────────
  // Each entry is a template clone. Only three knobs vary per language:
  //   - voices' langCode          (parity with Sarvam target)
  //   - sttSourceLanguage         (ISO-639-1)
  //   - translationStep.targetLang  (Sarvam Mayura target)
  {
    code: 'hi', label: 'Hindi', nativeLabel: 'हिंदी',
    region: 'indian', voices: openaiRt('hi-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('hi-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'hi',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'hi-IN', mode: 'classic-colloquial' },
    sampleText: 'नमस्ते! Learnbee में आपका स्वागत है। मैं आपको स्पष्टता और गर्मजोशी से इस पाठ्यक्रम में मार्गदर्शन करूंगी।',
  },
  {
    code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা',
    region: 'indian', voices: openaiRt('bn-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('bn-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'bn',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'bn-IN', mode: 'classic-colloquial' },
    sampleText: 'নমস্কার! Learnbee-তে আপনাকে স্বাগতম। আমি স্পষ্টতা এবং উষ্ণতার সাথে এই কোর্সে আপনাকে গাইড করব।',
  },
  {
    code: 'mr', label: 'Marathi', nativeLabel: 'मराठी',
    region: 'indian', voices: openaiRt('mr-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('mr-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'mr',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'mr-IN', mode: 'classic-colloquial' },
    sampleText: 'नमस्कार! Learnbee मध्ये आपले स्वागत आहे. मी स्पष्टतेने आणि उबदारपणाने या अभ्यासक्रमातून तुम्हाला मार्गदर्शन करेन.',
  },
  {
    code: 'gu', label: 'Gujarati', nativeLabel: 'ગુજરાતી',
    region: 'indian', voices: openaiRt('gu-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('gu-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'gu',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'gu-IN', mode: 'classic-colloquial' },
    sampleText: 'નમસ્તે! Learnbee માં આપનું સ્વાગત છે. હું તમને સ્પષ્ટતા અને હૂંફ સાથે આ કોર્સમાં માર્ગદર્શન આપીશ.',
  },
  {
    code: 'pa', label: 'Punjabi', nativeLabel: 'ਪੰਜਾਬੀ',
    region: 'indian', voices: openaiRt('pa-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('pa-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'pa',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'pa-IN', mode: 'classic-colloquial' },
    sampleText: 'ਸਤ ਸ੍ਰੀ ਅਕਾਲ! Learnbee ਵਿੱਚ ਤੁਹਾਡਾ ਸੁਆਗਤ ਹੈ। ਮੈਂ ਤੁਹਾਨੂੰ ਸਪੱਸ਼ਟਤਾ ਨਾਲ ਇਸ ਕੋਰਸ ਵਿੱਚ ਮਾਰਗਦਰਸ਼ਨ ਕਰਾਂਗੀ।',
  },
  {
    code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்',
    region: 'indian', voices: openaiRt('ta-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('ta-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'ta',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'ta-IN', mode: 'classic-colloquial' },
    sampleText: 'வணக்கம்! Learnbee-க்கு உங்களை வரவேற்கிறோம். தெளிவுடனும் அன்புடனும் இந்தப் பாடத்தில் நான் உங்களுக்கு வழிகாட்டுவேன்.',
  },
  {
    code: 'kn', label: 'Kannada', nativeLabel: 'ಕನ್ನಡ',
    region: 'indian', voices: openaiRt('kn-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('kn-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'kn',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'kn-IN', mode: 'classic-colloquial' },
    sampleText: 'ನಮಸ್ಕಾರ! Learnbee ಗೆ ಸ್ವಾಗತ. ನಾನು ನಿಮ್ಮನ್ನು ಸ್ಪಷ್ಟತೆ ಮತ್ತು ಪ್ರೀತಿಯಿಂದ ಈ ಕೋರ್ಸ್‌ನಲ್ಲಿ ಮಾರ್ಗದರ್ಶನ ಮಾಡುತ್ತೇನೆ.',
  },
  {
    code: 'ml', label: 'Malayalam', nativeLabel: 'മലയാളം',
    region: 'indian', voices: openaiRt('ml-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('ml-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'ml',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'ml-IN', mode: 'classic-colloquial' },
    sampleText: 'നമസ്കാരം! Learnbee-ലേക്ക് സ്വാഗതം. വ്യക്തതയോടും ഊഷ്മളതയോടും കൂടി ഈ കോഴ്സിലൂടെ ഞാൻ നിങ്ങളെ നയിക്കും.',
  },
  {
    code: 'te', label: 'Telugu', nativeLabel: 'తెలుగు',
    region: 'indian', voices: openaiRt('te-IN'), voiceTransport: 'openai-realtime',
    narrationVoices: sarvam('te-IN'),
    brainModel: 'gpt-4o', brainOutputLanguage: 'English', sttSourceLanguage: 'te',
    translationStep: { provider: 'sarvam-mayura', targetLang: 'te-IN', mode: 'classic-colloquial' },
    sampleText: 'నమస్కారం! Learnbee కి స్వాగతం. స్పష్టత మరియు ఆప్యాయతతో ఈ కోర్సులో నేను మిమ్మల్ని మార్గదర్శకత్వం చేస్తాను.',
  },

  // ── European + Japanese — full OpenAI RT ─────────────────────────────
  {
    code: 'es', label: 'Spanish', nativeLabel: 'Español',
    region: 'european', voices: openaiRt('es'), voiceTransport: 'openai-realtime',
    sttSourceLanguage: 'es',
    sampleText: 'Bienvenido a Learnbee. Te guiaré a través de este curso con claridad y calidez.',
  },
  {
    code: 'fr', label: 'French', nativeLabel: 'Français',
    region: 'european', voices: openaiRt('fr'), voiceTransport: 'openai-realtime',
    sttSourceLanguage: 'fr',
    sampleText: 'Bienvenue sur Learnbee. Je vous guiderai dans ce cours avec clarté et chaleur.',
  },
  {
    code: 'pt', label: 'Portuguese', nativeLabel: 'Português',
    region: 'european', voices: openaiRt('pt'), voiceTransport: 'openai-realtime',
    sttSourceLanguage: 'pt',
    sampleText: 'Bem-vindo ao Learnbee. Vou guiá-lo neste curso com clareza e calor.',
  },
  {
    code: 'de', label: 'German', nativeLabel: 'Deutsch',
    region: 'european', voices: openaiRt('de'), voiceTransport: 'openai-realtime',
    sttSourceLanguage: 'de',
    sampleText: 'Willkommen bei Learnbee. Ich werde Sie mit Klarheit und Wärme durch diesen Kurs führen.',
  },
  {
    code: 'ja', label: 'Japanese', nativeLabel: '日本語',
    region: 'east-asian', voices: openaiRt('ja'), voiceTransport: 'openai-realtime',
    sttSourceLanguage: 'ja',
    sampleText: 'Learnbeeへようこそ。明瞭さと温かさを持って、このコースをご案内します。',
  },
]

/**
 * Runtime remap from legacy codes to the canonical code. Belt-and-braces
 * for any DB row that migration 17 didn't reach — e.g. cached bundles,
 * embedded transcripts, or additional_languages entries missed by the
 * jsonb walk. Every legacy code either resolves to a canonical entry
 * here or falls back to English via getLanguage's default.
 *
 * All 17 aliases dropped in Request 3 map here.
 */
const LEGACY_CODE_REMAP: Record<string, string> = {
  'en-rt':     'en',
  'en-IN-rt':  'en-IN',
  'ta-hybrid': 'ta',
  'ta-rt':     'ta',
  'hi-hybrid': 'hi',
  'bn-hybrid': 'bn',
  'mr-hybrid': 'mr',
  'gu-hybrid': 'gu',
  'pa-hybrid': 'pa',
  'kn-hybrid': 'kn',
  'ml-hybrid': 'ml',
  'te-hybrid': 'te',
  'es-rt':     'es',
  'fr-rt':     'fr',
  'pt-rt':     'pt',
  'de-rt':     'de',
  'ja-rt':     'ja',
}

export const DEFAULT_LANGUAGE = 'en'
export const DEFAULT_VOICE_GENDER: VoiceGender = 'female'

/**
 * Look up a language by code. Falls back to English if not found.
 * Consults LEGACY_CODE_REMAP first so DB rows still carrying dropped
 * codes (e.g. `ta-hybrid`, `es-rt`) resolve to the canonical entry.
 */
export function getLanguage(code: string): Language {
  const canonical = LEGACY_CODE_REMAP[code] ?? code
  return LANGUAGES.find(l => l.code === canonical) ?? LANGUAGES[0]
}

/**
 * Language name to inject into the customer-brain prompt's "# Language\nSpeak in X"
 * instruction. Differs from `getLanguage(code).label` ONLY when the language
 * specifies `brainOutputLanguage` (Tamil Hybrid v2 → 'English'): in that case
 * the brain thinks in one language and the customer ultimately speaks another
 * via a post-brain translation step (sarvamTranslate).
 *
 * Use this at brain-prompt boundaries (customerTurn, startHybridSession).
 * Use `getLanguage(code).label` for finalize-report / UI display where the
 * user-facing language name is what matters.
 */
export function getBrainLanguageName(code: string): string {
  const lang = getLanguage(code)
  return lang.brainOutputLanguage ?? lang.label
}

/**
 * Returns the resolved TTS config for a language + gender.
 * If gender is omitted, defaults to female.
 */
export function getVoiceConfig(
  languageCode: string,
  gender: VoiceGender = DEFAULT_VOICE_GENDER,
): VoiceConfig {
  const lang = getLanguage(languageCode)
  return lang.voices[gender]
}

/**
 * Returns the resolved TTS config for COURSE NARRATION for a language +
 * gender. Prefers `narrationVoices` (Sarvam Bulbul v3 for en-IN + the 9
 * Indian languages → Indian accent) and falls back to `voices`.
 *
 * Use this on Content Creator surfaces (NarrationDrawer, wizard voice
 * preview, /play/, editor preview, translate/clone jobs). Role Play keeps
 * using getVoiceConfig — its realtime pipeline is locked to OpenAI voices.
 */
export function getNarrationVoiceConfig(
  languageCode: string,
  gender: VoiceGender = DEFAULT_VOICE_GENDER,
): VoiceConfig {
  const lang = getLanguage(languageCode)
  return (lang.narrationVoices ?? lang.voices)[gender]
}

/** Returns the sample text shown in the voice picker for a language. */
export function getSampleText(languageCode: string): string {
  return getLanguage(languageCode).sampleText ?? ENGLISH_SAMPLE
}

export const INDIAN_LANGUAGES    = LANGUAGES.filter(l => l.region === 'indian')
export const EUROPEAN_LANGUAGES  = LANGUAGES.filter(l => l.region === 'european')
export const EAST_ASIAN_LANGUAGES = LANGUAGES.filter(l => l.region === 'east-asian')

// ── Flags ─────────────────────────────────────────────────────────────────────
// Emoji flag per language, used by the translate picker + library deck cards.
// All Indian languages share the India flag; English (US) uses the US flag.
const LANGUAGE_FLAGS: Record<string, string> = {
  'en': '🇺🇸', 'en-IN': '🇮🇳',
  hi: '🇮🇳', bn: '🇮🇳', pa: '🇮🇳', mr: '🇮🇳', gu: '🇮🇳',
  ta: '🇮🇳', kn: '🇮🇳', ml: '🇮🇳', te: '🇮🇳',
  es: '🇪🇸', fr: '🇫🇷', pt: '🇧🇷', de: '🇩🇪',
  ja: '🇯🇵',
}

/** Emoji flag for a language code. Falls back by region, then to a globe. */
export function getFlag(code: string): string {
  if (LANGUAGE_FLAGS[code]) return LANGUAGE_FLAGS[code]
  const region = getLanguage(code).region
  return region === 'indian' ? '🇮🇳' : region === 'european' ? '🇪🇺' : '🌐'
}
